/**
 * Additive helpers for quiz question JSON upload.
 * Converts JSON → CSV Blob so the existing uploadCsv + csv-upload socket path stays unchanged.
 */

export const OBJECTIVE_CSV_HEADERS = [
  'ind_number',
  'question',
  'image_url',
  'equation',
  'tags',
  'difficulty_level',
  'weightage',
  'section',
  'paragaph',
  'related_video_id',
  'solution',
  'solution_equation',
  'solution_image_url',
  'option1',
  'equation1',
  'image_url1',
  'is_correct1',
  'option2',
  'equation2',
  'image_url2',
  'is_correct2',
  'option3',
  'equation3',
  'image_url3',
  'is_correct3',
  'option4',
  'equation4',
  'image_url4',
  'is_correct4',
  'configurations',
  'topics',
  'sub_topics',
  'learning_outcomes',
];

export const DAQ_CSV_HEADERS = [
  'ind_number',
  'question',
  'image_url',
  'equation',
  'tags',
  'difficulty_level',
  'question_type_level',
  'total_marks',
  'weightage',
  'section',
  'paragaph',
  'related_video_id',
  'solution',
  'solution_equation',
  'solution_image_url',
  'allow_ocr',
  'configurations',
  'topics',
  'sub_topics',
  'learning_outcomes',
];

export const SAMPLE_OBJECTIVE_QUESTIONS = [
  {
    ind_number: '1',
    question: 'What is the capital of Pakistan?',
    image_url: '',
    equation: '',
    tags: 'Geography+Pakistan',
    difficulty_level: 'Easy',
    weightage: '1',
    section: 'Geography',
    paragaph: '',
    related_video_id: '',
    solution: 'Islamabad is the capital city of Pakistan.',
    solution_equation: '',
    solution_image_url: '',
    option1: 'Karachi',
    equation1: '',
    image_url1: '',
    is_correct1: 'FALSE',
    option2: 'Islamabad',
    equation2: '',
    image_url2: '',
    is_correct2: 'TRUE',
    option3: 'Lahore',
    equation3: '',
    image_url3: '',
    is_correct3: 'FALSE',
    option4: 'Peshawar',
    equation4: '',
    image_url4: '',
    is_correct4: 'FALSE',
    configurations: 'General Knowledge Config',
    topics: 'Pakistan Studies',
    sub_topics: 'Capitals',
    learning_outcomes: 'LO1',
  },
  {
    ind_number: '2',
    question: 'Which gas do plants absorb during photosynthesis?',
    image_url: '',
    equation: '',
    tags: 'Biology+Plant',
    difficulty_level: 'Medium',
    weightage: '2',
    section: 'Biology',
    paragaph: '',
    related_video_id: '',
    solution: 'Plants absorb carbon dioxide (CO2) during photosynthesis.',
    solution_equation: '',
    solution_image_url: '',
    option1: 'Oxygen',
    equation1: '',
    image_url1: '',
    is_correct1: 'FALSE',
    option2: 'Nitrogen',
    equation2: '',
    image_url2: '',
    is_correct2: 'FALSE',
    option3: 'Carbon dioxide',
    equation3: '',
    image_url3: '',
    is_correct3: 'TRUE',
    option4: 'Hydrogen',
    equation4: '',
    image_url4: '',
    is_correct4: 'FALSE',
    configurations: 'Biology Config',
    topics: 'Photosynthesis',
    sub_topics: 'Light Reaction|Dark Reaction',
    learning_outcomes: 'LO1|LO2',
  },
];

export const SAMPLE_DAQ_QUESTIONS = [
  {
    ind_number: '1',
    question: 'Explain the process of photosynthesis in plants.',
    image_url: '',
    equation: '',
    tags: 'Biology+Plant',
    difficulty_level: 'Medium',
    question_type_level: 'long',
    total_marks: '100',
    weightage: '5',
    section: 'Biology',
    paragaph: '',
    related_video_id: '',
    solution: 'Write a complete explanation covering light and dark reactions.',
    solution_equation: '',
    solution_image_url: '',
    allow_ocr: 'TRUE',
    configurations: 'Biology Config',
    topics: 'Photosynthesis',
    sub_topics: 'Light Reaction|Dark Reaction',
    learning_outcomes: 'LO1|LO2',
  },
  {
    ind_number: '2',
    question: "Define Newton's second law of motion.",
    image_url: '',
    equation: '',
    tags: 'Physics',
    difficulty_level: 'Easy',
    question_type_level: 'short',
    total_marks: '50',
    weightage: '3',
    section: 'Physics',
    paragaph: '',
    related_video_id: '',
    solution: 'F = ma',
    solution_equation: '',
    solution_image_url: '',
    allow_ocr: 'TRUE',
    configurations: 'Physics Config',
    topics: 'Laws of Motion',
    sub_topics: 'Force',
    learning_outcomes: 'LO3',
  },
  {
    ind_number: '3',
    question: 'A car accelerates from 0 to 20 m/s in 5 s. Find acceleration.',
    image_url: '',
    equation: '',
    tags: 'Physics+Numerical',
    difficulty_level: 'Hard',
    question_type_level: 'numerical',
    total_marks: '100',
    weightage: '5',
    section: 'Physics',
    paragaph: '',
    related_video_id: '',
    solution: 'a = (v-u)/t = 4 m/s^2',
    solution_equation: '',
    solution_image_url: '',
    allow_ocr: 'TRUE',
    configurations: 'Physics Config',
    topics: 'Kinematics',
    sub_topics: 'Acceleration',
    learning_outcomes: 'LO4',
  },
];

function escapeCSV(field) {
  if (field === null || field === undefined) return '';
  let value = field.toString();
  if (value.includes('"')) {
    value = value.replace(/"/g, '""');
  }
  if (value.includes(',') || value.includes('\n') || value.includes('\r')) {
    value = `"${value}"`;
  }
  return value;
}

function boolToCsvFlag(value) {
  if (value === true || value === 1) return 'TRUE';
  if (value === false || value === 0) return 'FALSE';
  if (value === null || value === undefined || value === '') return '';
  const raw = String(value).trim().toUpperCase();
  if (['TRUE', 'YES', '1'].includes(raw)) return 'TRUE';
  if (['FALSE', 'NO', '0'].includes(raw)) return 'FALSE';
  return String(value);
}

function listToPipe(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
        if (typeof item === 'object' && item.name) return String(item.name).trim();
        return String(item).trim();
      })
      .filter(Boolean)
      .join('|');
  }
  if (value == null) return '';
  return String(value);
}

function tagsToPlus(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'object' && item.tag_name) return String(item.tag_name).trim();
        return String(item).trim();
      })
      .filter(Boolean)
      .join('+');
  }
  if (value == null) return '';
  return String(value);
}

/**
 * Accepts either a top-level array or `{ questions: [...] }`.
 * Supports flat CSV-like keys or nested `options: [{ option, equation, image_url, is_correct }]`.
 */
export function normalizeQuizJsonQuestions(parsed, quizType = 'full') {
  let questions = parsed;
  if (parsed && !Array.isArray(parsed) && Array.isArray(parsed.questions)) {
    questions = parsed.questions;
  }
  if (!Array.isArray(questions) || questions.length === 0) {
    throw new Error(
      'JSON must be a non-empty array of questions, or an object with a non-empty "questions" array.',
    );
  }

  const isDaq = quizType === 'daq';
  return questions.map((raw, index) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      throw new Error(`Question at index ${index} must be an object.`);
    }

    const row = { ...raw };

    // Nested options → flat option1…option4 columns used by the CSV socket parser
    if (Array.isArray(raw.options) && raw.options.length) {
      for (let i = 0; i < 4; i += 1) {
        const opt = raw.options[i] || {};
        const n = i + 1;
        if (row[`option${n}`] === undefined) row[`option${n}`] = opt.option != null ? opt.option : '';
        if (row[`equation${n}`] === undefined) row[`equation${n}`] = opt.equation != null ? opt.equation : '';
        if (row[`image_url${n}`] === undefined) {
          row[`image_url${n}`] = opt.image_url != null ? opt.image_url : '';
        }
        if (row[`is_correct${n}`] === undefined) {
          row[`is_correct${n}`] = boolToCsvFlag(opt.is_correct);
        }
      }
    }

    // Normalize boolean-ish flags used by CSV parser
    for (let n = 1; n <= 4; n += 1) {
      if (row[`is_correct${n}`] !== undefined && row[`is_correct${n}`] !== '') {
        row[`is_correct${n}`] = boolToCsvFlag(row[`is_correct${n}`]);
      }
    }
    if (row.allow_ocr !== undefined && row.allow_ocr !== '') {
      row.allow_ocr = boolToCsvFlag(row.allow_ocr);
    }

    row.tags = tagsToPlus(row.tags);
    row.configurations = listToPipe(row.configurations);
    row.topics = listToPipe(row.topics);
    row.sub_topics = listToPipe(row.sub_topics || row.subtopics || row['sub-topics']);
    row.learning_outcomes = listToPipe(row.learning_outcomes);

    // Ensure every required CSV column exists (empty string is fine)
    const headers = isDaq ? DAQ_CSV_HEADERS : OBJECTIVE_CSV_HEADERS;
    headers.forEach((key) => {
      if (row[key] === undefined || row[key] === null) {
        row[key] = '';
      } else if (typeof row[key] !== 'string') {
        row[key] = String(row[key]);
      }
    });

    return row;
  });
}

export function questionsToCsvBlob(questions, quizType = 'full') {
  const headers = quizType === 'daq' ? DAQ_CSV_HEADERS : OBJECTIVE_CSV_HEADERS;
  const lines = [
    headers.join(','),
    ...questions.map((row) => headers.map((key) => escapeCSV(row[key])).join(',')),
  ];
  return new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
}

export function questionsToCsvFile(questions, quizType = 'full', fileName) {
  const blob = questionsToCsvBlob(questions, quizType);
  const name =
    fileName ||
    (quizType === 'daq' ? 'quiz-questions-daq-from-json.csv' : 'quiz-questions-from-json.csv');
  return new File([blob], name, { type: 'text/csv' });
}

export async function convertQuizJsonFileToCsv(file, quizType = 'full') {
  const text = await file.text();
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    throw new Error('Invalid JSON file — please fix the syntax and try again.');
  }
  const questions = normalizeQuizJsonQuestions(parsed, quizType);
  const baseName = (file.name || 'quiz').replace(/\.json$/i, '');
  return questionsToCsvFile(questions, quizType, `${baseName}.csv`);
}

export function isQuizJsonFile(file) {
  if (!file) return false;
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.json')) return true;
  const type = (file.type || '').toLowerCase();
  return type === 'application/json' || type === 'text/json';
}

function triggerJsonDownload(data, fileName) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadSampleQuizJson(quizType = 'full') {
  if (quizType === 'daq') {
    triggerJsonDownload(
      {
        quiz_type: 'daq',
        format_version: '1.0',
        questions: SAMPLE_DAQ_QUESTIONS,
      },
      'sample-nearpeer-daq-quiz.json',
    );
    return;
  }
  triggerJsonDownload(
    {
      quiz_type: 'full',
      format_version: '1.0',
      questions: SAMPLE_OBJECTIVE_QUESTIONS,
    },
    'sample-nearpeer-objective-quiz.json',
  );
}
